import { forwardRef, type JSX } from 'react';

import { useInputIds } from '../../../hooks/useInputIds';
import { CheckIcon } from '../../../icons';
import { Box } from '../../Box/Box';
import { Grid } from '../../Grid/Grid';
import { Stack } from '../../Stack/Stack';
import { Text } from '../../Text/Text';
import { Props } from '../Checkbox';
import { checkboxInvalidStyle, checkboxStyle, checkedStateEnabledStyle, checkedStateStyle } from '../Checkbox.css';
import {
  checkTileCheckboxStyle,
  checkTileCheckedStateStyle,
  checkTileLabelStyle,
  checkTileLabelWrapperStyle,
} from './CheckTile.css';

type CheckTileProps = Omit<Props, 'label'> & {
  children: string;
  icon?: JSX.Element;
  subtitle?: string;
};

export const CheckTile = forwardRef<HTMLInputElement, CheckTileProps>(
  ({ children, error, hint, isChecked, icon, subtitle, ...restProps }, ref) => {
    const { inputId, describedBy } = useInputIds({ error, hint });

    const baseInputClassName = `${checkboxStyle} ${checkTileCheckboxStyle}`;
    const inputClassName = error ? `${baseInputClassName} ${checkboxInvalidStyle}` : baseInputClassName;

    return (
      <Stack gap="2">
        <label className={checkTileLabelStyle}>
          <Box paddingX="4">
            <Stack gap="3" direction="row" alignY="center">
              <Grid gridTemplateColumns="min-content auto" gridTemplateRows="1fr">
                <input
                  ref={ref}
                  type="checkbox"
                  id={inputId}
                  aria-describedby={describedBy}
                  aria-invalid={error ? true : undefined}
                  disabled={false}
                  checked={isChecked}
                  className={inputClassName}
                  {...restProps}
                />
                <div className={`${checkedStateStyle} ${checkTileCheckedStateStyle} ${checkedStateEnabledStyle}`}>
                  <CheckIcon color="iconInverted" size="small" />
                </div>
              </Grid>
              <span className={checkTileLabelWrapperStyle}>
                <Stack gap="3" direction="row" alignY="center">
                  {icon ? <Stack.Item shrink={false}>{icon}</Stack.Item> : null}
                  <Box paddingY="3">
                    <Text weight="bold">{children}</Text>
                    {subtitle && (
                      <Text weight="regular" size="BodyM">
                        {subtitle}
                      </Text>
                    )}
                  </Box>
                </Stack>
              </span>
            </Stack>
          </Box>
        </label>
      </Stack>
    );
  },
);

CheckTile.displayName = 'CheckTile';
